import { ref, watch } from "vue";

import POSConfigModule from "@/store/modules/pos-config";
import DeviceModule from "@/store/modules/device";
import CashFloatModule from "@/store/modules/cash-float";
import {
  useFeatureFlagStore,
  getClient,
  ffObservability,
  FeatureFlagKeys,
  FLAGSMITH_API_URL,
  FLAGSMITH_APP_PREFIX,
} from "@/feature-flags";
import { LocalStorageKeys } from "@/services/storage.service";

const config = {
  environmentID: String(import.meta.env.VITE_FLAGSMITH_ENV_KEY),
  apiUrl: FLAGSMITH_API_URL,
  appPrefix: FLAGSMITH_APP_PREFIX,
  verbose: import.meta.env.VITE_ENV !== "prod",
  observability: ffObservability,
};

// The self-hosted Flagsmith can time out or error on init.
// Retry a few times so a transient failure at boot self-heals, defaulting to
// static endpoints meanwhile.
const MAX_INIT_ATTEMPTS = 3;
const RETRY_DELAY_MS = 5000;
const FLAGS_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const MIN_REFRESH_GAP_MS = 30 * 1000;

// Bumped after each successful fetch so flag getters recompute: resolved flags
// live in the (non-reactive) OpenFeature client, and isReady never changes on a
// same-group refetch, so nothing else would trigger reactivity.
const flagsRevision = ref(0);
function markFlagsRefreshed(): void {
  rememberLastKnownFlags();
  flagsRevision.value++;
}

// The store stays ready after a failed identity fetch: require a READY provider
function hasLiveFlagsFor(groupId: number): boolean {
  const store = useFeatureFlagStore();
  return (
    store.isReady &&
    store.lastContext?.groupId === groupId &&
    String(getClient()?.providerStatus) === "READY"
  );
}

// Last resolved cashbook flags per group, used while Flagsmith cannot answer
const LAST_KNOWN_FLAG_KEYS = [
  FeatureFlagKeys.CashbookV2,
  FeatureFlagKeys.MultiCurrency,
] as const;
type LastKnownFlagKey = (typeof LAST_KNOWN_FLAG_KEYS)[number];
type LastKnownFlags = Record<
  string,
  Partial<Record<LastKnownFlagKey, boolean>>
>;

// Read each time: the customer display window writes the same entry
function readLastKnownFlags(): LastKnownFlags {
  try {
    const stored: unknown = JSON.parse(
      localStorage.getItem(LocalStorageKeys.FeatureFlags) || "{}"
    );
    return stored && typeof stored === "object" && !Array.isArray(stored)
      ? (stored as LastKnownFlags)
      : {};
  } catch {
    // Corrupted entry: the next fetch rewrites it
    return {};
  }
}

function rememberLastKnownFlags(): void {
  const groupId = POSConfigModule.groupId;
  if (!groupId || !hasLiveFlagsFor(groupId)) {
    return;
  }
  const store = useFeatureFlagStore();
  const all = readLastKnownFlags();
  all[String(groupId)] = Object.fromEntries(
    LAST_KNOWN_FLAG_KEYS.map((key) => [key, store.isEnabled(key)])
  );
  try {
    localStorage.setItem(LocalStorageKeys.FeatureFlags, JSON.stringify(all));
  } catch {
    // Storage full or blocked: live flags still apply
  }
}

function isEnabledOrLastKnown(key: LastKnownFlagKey): boolean {
  const store = useFeatureFlagStore();
  // Track the revision so callers recompute when a refresh changes flag values.
  void flagsRevision.value;

  const groupId = POSConfigModule.groupId;
  if (!groupId) {
    return false;
  }
  if (hasLiveFlagsFor(groupId)) {
    return store.isEnabled(key);
  }
  return readLastKnownFlags()[String(groupId)]?.[key] === true;
}

// Flags target by group only; single source for the identify context.
const groupContext = (groupId: number) => ({
  targetingKey: String(groupId),
  groupId,
});

async function initWithRetry(groupId: number): Promise<void> {
  const store = useFeatureFlagStore();

  for (let attempt = 1; attempt <= MAX_INIT_ATTEMPTS; attempt++) {
    // Abandon if the group changed or the flags arrived via another path.
    if (POSConfigModule.groupId !== groupId || hasLiveFlagsFor(groupId)) {
      return;
    }
    // Failed fetches don't throw; once ready, init is a no-op, so re-identify
    const context = groupContext(groupId);
    await (store.isReady
      ? store.updateContext(context)
      : store.init({ config, context })
    ).catch(() => undefined);
    if (hasLiveFlagsFor(groupId)) {
      markFlagsRefreshed();
      return;
    }
    if (attempt < MAX_INIT_ATTEMPTS) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    }
  }
}

let initsRunning = 0;
async function trackedInit(groupId: number): Promise<void> {
  initsRunning++;
  try {
    await initWithRetry(groupId);
  } finally {
    initsRunning--;
  }
}

let refreshInFlight = false;
let lastRefreshMs = 0;

// Re-sync flags so segment changes reach an open POS without a reload.
// Guarded so overlapping focus/interval triggers cannot stack racing requests
// or resolve out of order. force=true bypasses the min-gap throttle for an
// explicit, event-driven refresh (e.g. arriving at the login screen).
export function refreshFlags(force = false): void {
  const store = useFeatureFlagStore();
  const groupId = POSConfigModule.groupId;
  const now = Date.now();

  if (
    initsRunning > 0 ||
    !groupId ||
    refreshInFlight ||
    (!force && now - lastRefreshMs < MIN_REFRESH_GAP_MS)
  ) {
    return;
  }

  refreshInFlight = true;
  lastRefreshMs = now;
  // Also retries a failed fetch, so a till started during an outage recovers
  const sync = hasLiveFlagsFor(groupId)
    ? store.updateContext(groupContext(groupId)).then(markFlagsRefreshed)
    : trackedInit(groupId);
  void sync
    .catch(() => undefined)
    .finally(() => {
      refreshInFlight = false;
    });
}

let ffSetupInstalled = false;

// POS targets flags by group only, and groupId is known only after login.
// Init when it first resolves, re-identify on group switches, fail closed.
export function setupFeatureFlags(): void {
  if (ffSetupInstalled) {
    return;
  }
  ffSetupInstalled = true;

  watch(
    () => POSConfigModule.groupId,
    (groupId) => {
      if (!groupId) {
        return;
      }
      // First init and group switches alike, retried until the flags resolve
      void trackedInit(groupId);
    },
    // immediate so a reload, where groupId is already restored from
    // localStorage before this watcher registers, still initializes.
    { immediate: true }
  );

  // POS terminals can stay open for days, keep flags fresh in the background
  // and re-sync when the window regains focus. Wrapped so the focus Event is
  // not passed as the force argument.
  setInterval(() => refreshFlags(), FLAGS_REFRESH_INTERVAL_MS);
  window.addEventListener("focus", () => refreshFlags());
}

// Cashbook V2 rollout per group (pos::cash-book-v2)
export function isCashbookV2Enabled(): boolean {
  return isEnabledOrLastKnown(FeatureFlagKeys.CashbookV2);
}

// Multi-currency on devices linked to a cash location (pos::multi-currency)
export function isMultiCurrencyEnabled(): boolean {
  return isEnabledOrLastKnown(FeatureFlagKeys.MultiCurrency);
}

// The opening/closing/transfer flows assume a device linked to a cash
// location, so they fall back to the legacy V1 UI when unlinked - even
// though the rest of the V2 experience (e.g. the "sales closed" banner)
// still shows for an eligible group.
export function isCashbookV2FlowEnabled(): boolean {
  return isCashbookV2Enabled() && !!DeviceModule.hardwareLocation;
}

// Per-group exemption from the FIS-42 web POS block: POS flags are identified
// by groupId, so enabling pos::web-pos-fiscal-exemption for a segment lets
// that group keep using the web POS regardless of its country (e.g. demos).
// Fails closed — no exemption while Flagsmith is unreachable or the flag is
// absent — so an outage can never silently re-enable non-compliant web sales.
export function isWebPosFiscalExempt(): boolean {
  const store = useFeatureFlagStore();
  // Track the revision so callers recompute when a refresh changes flag values.
  void flagsRevision.value;

  const exempt =
    store.isReady && store.isEnabled(FeatureFlagKeys.WebPosFiscalExemption);
  console.log("[FIS-42] isWebPosFiscalExempt", {
    flagsmithReady: store.isReady,
    flagEnabledForGroup: store.isReady
      ? store.isEnabled(FeatureFlagKeys.WebPosFiscalExemption)
      : "(store not ready)",
    groupId: POSConfigModule.groupId,
    exempt,
  });
  return exempt;
}

// FIS-374 gradual rollout of the compliance-common Austria pipeline. POS flags
// are identified by groupId, so enabling pos::austria-compliance-package for a
// segment moves that group from the legacy signAustriaReceipt GraphQL mutation
// to the package pipeline (initializeComplianceLib -> processSaleEvent), while
// every other Austrian group keeps signing through the backend. Fails closed:
// while Flagsmith is unreachable or the flag is absent, the group stays on the
// legacy path, which is what every Austrian group runs today.
export function isAustriaCompliancePackageEnabled(): boolean {
  const store = useFeatureFlagStore();
  // Track the revision so callers recompute when a refresh changes flag values.
  void flagsRevision.value;

  return (
    store.isReady && store.isEnabled(FeatureFlagKeys.AustriaCompliancePackage)
  );
}

// FIS-529 rollout gate: print receipts through @smeetz/receipt instead of the
// legacy template. Fails closed — legacy prints while Flagsmith is unreachable
// or the flag is absent — so an outage can never change what a till prints.
export function isUnifiedReceiptEnabled(): boolean {
  const store = useFeatureFlagStore();
  // Track the revision so callers recompute when a refresh changes flag values.
  void flagsRevision.value;
  return store.isReady && store.isEnabled(FeatureFlagKeys.UnifiedReceipt);
}

// FIS-715 rollout gate for terminal tipping: the card terminal asks for a tip only
// for groups with pos::tipping enabled. Fails closed — no tip screen while
// Flagsmith is unreachable or the flag is absent — so an outage can never start
// asking customers for tips.
export function isTippingEnabled(): boolean {
  const store = useFeatureFlagStore();
  // Track the revision so callers recompute when a refresh changes flag values.
  void flagsRevision.value;

  return store.isReady && store.isEnabled(FeatureFlagKeys.Tipping);
}

// PIN login needs only groupId: from the selected config, or from
// an open cashfloat session (which implies a config was already loaded).
export function pinTerminalGroupId(): number | null {
  return (
    POSConfigModule.selectedConfig?.group ??
    CashFloatModule.cashFloat?.groupId ??
    null
  );
}

// Rollout gate for the check-in feature. Off keeps the legacy scan, its
// auto-scan toggle and the QR code jump to the basket (APPS-509).
export function isCheckInEnabled(): boolean {
  const store = useFeatureFlagStore();
  // Track the revision so this recomputes when a refresh changes flag values.
  void flagsRevision.value;

  return store.isReady && store.isEnabled(FeatureFlagKeys.PosCheckIn);
}

// Rollout gate shared by the login screen and the navbar lock shortcut: enable
// PIN login only for a known group with the flag enabled.
export function isPinLoginEnabled(): boolean {
  const store = useFeatureFlagStore();
  // Track the revision so this recomputes when a refresh changes flag values.
  void flagsRevision.value;

  return (
    !!pinTerminalGroupId() &&
    store.isReady &&
    store.isEnabled(FeatureFlagKeys.PosSellerPin)
  );
}
